/**
 * Widget Dashboard « Rendement par parcelle » — branché sur `dashboard.rendement`
 * (Lot H.3, cadrage métier § 1).
 *
 * RÈGLE MÉTIER TENUE (non négociable) :
 *   - 3 zones : `dans_le_disponible` (✓ vine), `excedent_reserve` (● ambre —
 *     excédent LÉGAL à loger en réserve, PAS une faute), `hors_aoc` (✗ rust —
 *     au-dessus du butoir). `non_renseigne` → NI jauge NI couleur.
 *   - La LIGNE TOTAL EXPLOITATION fait foi (c'est là que la conformité s'apprécie,
 *     DR) ; la parcelle est seulement INDICATIVE.
 *   - Toujours icône + mot (jamais la couleur seule : soleil / daltonisme).
 *
 * Gating : masqué sans `vendange.pesee_read` (fail-closed). Si aucune campagne de
 * vendange (NOT_FOUND / cause AUCUNE_CAMPAGNE), on affiche un état vide propre,
 * pas une erreur rouge.
 */

import { extractTrpcCode } from "@champ-erp/auth-primitives/web";
import type {
  RendementParcelle,
  RendementParcelleGlobalSortie,
  RendementZone,
} from "@champ-erp/contracts";
import { RENDEMENT_ZONE_LABELS } from "@champ-erp/contracts";
import { Card, ProgressBar, Skeleton, WidgetHeader, type ProgressTone } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  CircleDot,
  MinusCircle,
  Scale,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { useCan } from "../../auth/use-can.js";
import { HelpBubble } from "../../components/aide/HelpBubble.js";
import { trpc } from "../../lib/trpc.js";

/** Icône + couleur (texte) + tone de jauge par zone. `tone: null` → pas de jauge. */
const ZONE_CONFIG: Record<
  RendementZone,
  { Icon: LucideIcon; colorClass: string; tone: ProgressTone | null }
> = {
  dans_le_disponible: { Icon: CheckCircle2, colorClass: "text-vine-700", tone: "vine" },
  excedent_reserve: { Icon: CircleDot, colorClass: "text-champagne-700", tone: "gold" },
  hors_aoc: { Icon: XCircle, colorClass: "text-rust-700", tone: "rust" },
  non_renseigne: { Icon: MinusCircle, colorClass: "text-champagne-600", tone: null },
};

/** Rendement kg/ha → « 8 400 kg/ha » ; null (surface nulle) → « — ». */
function formatKgHa(valeur: number | null): string {
  if (valeur === null) return "—";
  return `${Math.round(valeur).toLocaleString("fr-FR")} kg/ha`;
}

/**
 * Remplissage de la jauge (0..100) : rapporté au BUTOIR (plafond AOC) si connu,
 * sinon au disponible. Donne une échelle commune aux 3 zones. Sans référence
 * (année non publiée) → 0 (aucune jauge affichée de toute façon).
 */
function remplissage(
  rendement: number | null,
  disponible: number | null,
  butoir: number | null,
): number {
  if (rendement === null) return 0;
  const reference = butoir ?? disponible;
  if (reference === null || reference <= 0) return 0;
  return Math.max(0, Math.min(100, (rendement / reference) * 100));
}

export function WidgetRendementParcelle() {
  const { granted, isLoading: permLoading } = useCan("vendange.pesee_read");
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["dashboard.rendement"],
    queryFn: () => trpc.dashboard.rendement.query(),
    enabled: granted,
    retry: false,
  });

  if (permLoading || !granted) return null;
  if (isError && extractTrpcCode(error) === "FORBIDDEN") return null;

  // Aucune campagne de vendange → état vide honnête (pas une erreur rouge).
  const aucuneCampagne = isError && extractTrpcCode(error) === "NOT_FOUND";

  return (
    <Card as="section" className="p-6">
      <WidgetHeader
        eyebrow="Vignoble · Rendement"
        title="Rendement par parcelle"
        titleHelp={
          <HelpBubble articleSlug="le-rendement-de-l-annee" label="Aide : rendement de l'année" />
        }
        action={{
          label: "Voir les pesées",
          href: "/vendange",
          onClick: (event) => {
            event.preventDefault();
            navigate("/vendange");
          },
        }}
      />
      {aucuneCampagne ? (
        <EtatSansCampagne />
      ) : isLoading ? (
        <Skeleton variant="text" lines={4} />
      ) : isError || !data ? (
        <p className="rounded-lg border border-dashed border-champagne-200 bg-champagne-50/40 px-4 py-6 text-center text-[13px] text-champagne-700">
          Impossible de charger les rendements pour l&apos;instant.
        </p>
      ) : (
        <RendementContenu data={data} />
      )}
    </Card>
  );
}

/** État vide : aucune campagne de vendange ouverte → invitation. */
function EtatSansCampagne() {
  return (
    <div className="flex flex-col items-center gap-3 py-6 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-champagne-50 text-champagne-700 ring-1 ring-champagne-200">
        <Scale size={26} />
      </div>
      <p className="text-[14px] text-champagne-800">Aucune campagne de vendange enregistrée.</p>
      <Link
        to="/vendange"
        className="text-[13px] font-medium text-champagne-700 underline-offset-2 hover:text-champagne-900 hover:underline"
      >
        Ouvrir une campagne →
      </Link>
    </div>
  );
}

function RendementContenu({ data }: { data: RendementParcelleGlobalSortie }) {
  const { rendement_disponible_kg_ha: disponible, rendement_butoir_kg_ha: butoir } = data;

  // Rappel des seuils de l'année (jamais hardcodés — lus depuis la campagne).
  const rappelSeuils =
    disponible !== null
      ? `Disponible ${formatKgHa(disponible)}` +
        (butoir !== null ? ` · butoir ${formatKgHa(butoir)}` : "")
      : "Rendement de l'année non renseigné";

  // Aucune pesée cette campagne → invitation (pas une grille de « 0 »).
  if (data.parcelles.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-[12px] text-champagne-700">
          Millésime <span className="mono tnum">{data.millesime}</span> · {rappelSeuils}
        </p>
        <p className="rounded-lg border border-dashed border-champagne-200 bg-champagne-50/40 px-4 py-6 text-center text-[13px] text-champagne-700">
          Aucune pesée enregistrée pour cette campagne. Les rendements s&apos;afficheront au fil de
          la vendange.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-[12px] text-champagne-700">
        Millésime <span className="mono tnum">{data.millesime}</span> · {rappelSeuils}
      </p>

      {data.parcelles.map((parcelle) => (
        <LigneRendement
          key={parcelle.parcelle_id}
          libelle={parcelle.parcelle_libelle}
          cepage={parcelle.cepage}
          rendement={parcelle.rendement_reel_kg_ha}
          zone={parcelle.zone}
          disponible={disponible}
          butoir={butoir}
        />
      ))}

      {/* Total exploitation = la RÉFÉRENCE de conformité (DR) — mis en avant. */}
      <div className="border-t border-champagne-900/10 pt-4">
        <LigneRendement
          libelle="Total exploitation"
          cepage={null}
          rendement={data.total_exploitation.rendement_reel_kg_ha}
          zone={data.total_exploitation.zone}
          disponible={disponible}
          butoir={butoir}
          referent
        />
        <p className="mt-1.5 text-[11px] text-champagne-600">
          Niveau qui fait foi pour la conformité (déclaration de récolte). Les lignes par parcelle
          sont indicatives.
        </p>
      </div>
    </div>
  );
}

/**
 * Une ligne de rendement (parcelle ou total). `referent` = mise en avant
 * typographique de la ligne exploitation.
 */
function LigneRendement({
  libelle,
  cepage,
  rendement,
  zone,
  disponible,
  butoir,
  referent = false,
}: {
  libelle: string;
  cepage: RendementParcelle["cepage"];
  rendement: number | null;
  zone: RendementZone;
  disponible: number | null;
  butoir: number | null;
  referent?: boolean;
}) {
  const config = ZONE_CONFIG[zone];
  const { Icon } = config;
  const fill = remplissage(rendement, disponible, butoir);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span
          className={
            referent
              ? "text-[13px] font-semibold uppercase tracking-wide text-aubergine-900"
              : "text-[14px] font-medium text-aubergine-900"
          }
        >
          {libelle}
          {cepage && (
            <span className="ml-1.5 text-[12px] font-normal text-champagne-700">· {cepage}</span>
          )}
        </span>
        <span
          className={`mono tnum shrink-0 ${referent ? "text-[16px]" : "text-[15px]"} font-semibold text-aubergine-900`}
        >
          {formatKgHa(rendement)}
        </span>
      </div>

      {/* Icône + MOT de la zone (jamais la couleur seule). */}
      <div className={`mt-1 flex items-center gap-1.5 text-[12px] ${config.colorClass}`}>
        <Icon size={14} />
        <span>{RENDEMENT_ZONE_LABELS[zone]}</span>
      </div>

      {/* Jauge seulement si la zone est renseignée (sinon ni jauge ni couleur). */}
      {config.tone !== null && (
        <ProgressBar
          value={fill}
          tone={config.tone}
          label={`${libelle} : ${formatKgHa(rendement)}`}
          className="mt-2"
        />
      )}
    </div>
  );
}
